import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  Alert,
  Paper,
} from "@mui/material";
import { Add, Edit, Delete, Close, Image as ImageIcon } from "@mui/icons-material";
import { useEffect, useState } from "react";
import useFetchDataWithPagination from "../../../utility/useFetchDataWithPagination";
import usePostData from "../../../utility/usePostData";

const GalleryDirectionManagement = () => {
  const { isLoading, error, submitData } = usePostData();
  const { data, isLoading: loading, paginate, refreshSearch } =
    useFetchDataWithPagination("gallery-directions");

  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [galleryList, setGalleryList] = useState([]);

  const [formData, setFormData] = useState({
    name_en: "",
    name_fr: "",
    description_en: "",
    description_fr: "",
  });

  useEffect(() => {
    if (data?.data?.data) {
      setGalleryList(data.data.data);
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditMode(true);
      setCurrentItem(item);
      setFormData({
        name_en: item.name_en || "",
        name_fr: item.name_fr || "",
        description_en: item.description_en || "",
        description_fr: item.description_fr || "",
      });
      setPreviewUrl(item.image);
    } else {
      setEditMode(false);
      setFormData({
        name_en: "",
        name_fr: "",
        description_en: "",
        description_fr: "",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrorMessage("");
    setSelectedFile(null);
    setPreviewUrl(null);
    setCurrentItem(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name_en && !formData.name_fr) {
      setErrorMessage("Veuillez renseigner au moins un nom (EN ou FR).");
      return;
    }

    const form = new FormData();
    form.append("name_en", formData.name_en);
    form.append("name_fr", formData.name_fr);
    form.append("description_en", formData.description_en);
    form.append("description_fr", formData.description_fr);

    if (selectedFile) {
      form.append("image", selectedFile);
    }

    const endpoint = editMode
      ? `gallery-directions/${currentItem.id}`
      : "gallery-directions";
    if (editMode) form.append("_method", "PUT");

    submitData(endpoint, form);
    refreshSearch();
    handleCloseDialog();
  };

  const handleDelete = (id) => {
    if (window.confirm("Supprimer cette image ?")) {
      submitData(`gallery-directions/${id}`, null, "DELETE");
      refreshSearch();
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isLoading && <LinearProgress color="primary" sx={{ mb: 2 }} />}

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <ImageIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h4" fontWeight={700} color="primary">
            Galerie - Directions
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nouvelle image
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message}
        </Alert>
      )}

      {loading && <LinearProgress color="secondary" sx={{ mb: 2 }} />}

      {/* Liste */}
      <Grid container spacing={3}>
        {Array.isArray(galleryList) && galleryList.length > 0 ? (
          galleryList.map((item, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
                }}
              >
                {item.image_url && (
                  <CardMedia
                    component="img"
                    height="250"
                    image={item.image_url}
                    alt={item.name_fr || item.name_en}
                    sx={{ objectFit: "cover" }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {item.name_fr || item.name_en}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {item.description_fr || item.description_en}
                  </Typography>
                </CardContent>

                <Box sx={{ p: 2, pt: 0 }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(item)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                textAlign: "center",
                py: 8,
                bgcolor: "grey.50",
                borderRadius: 2,
              }}
            >
              <ImageIcon sx={{ fontSize: 80, color: "grey.300", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Aucune image dans la galerie
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ mt: 2 }}
              >
                Ajouter la première image
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        {paginate()}
      </Box>

      {/* Formulaire */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editMode ? "Modifier l’image" : "Nouvelle image de la galerie"}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nom (EN)"
              name="name_en"
              value={formData.name_en}
              onChange={handleInputChange}
            />
            <TextField
              label="Nom (FR)"
              name="name_fr"
              value={formData.name_fr}
              onChange={handleInputChange}
            />
            <TextField
              label="Description (EN)"
              name="description_en"
              value={formData.description_en}
              onChange={handleInputChange}
              multiline
              rows={3}
            />
            <TextField
              label="Description (FR)"
              name="description_fr"
              value={formData.description_fr}
              onChange={handleInputChange}
              multiline
              rows={3}
            />

            {previewUrl && (
              <Box sx={{ position: "relative" }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{
                    width: "100%",
                    maxHeight: "300px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
                {!editMode && (
                  <IconButton
                    onClick={removeImage}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "error.main",
                      color: "white",
                      "&:hover": { bgcolor: "error.dark" },
                    }}
                    size="small"
                  >
                    <Close />
                  </IconButton>
                )}
              </Box>
            )}

            {!previewUrl && (
              <Button variant="outlined" component="label" fullWidth>
                Sélectionner une image *
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoading}>
            {editMode ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GalleryDirectionManagement;
